import { FormikProps } from "formik";
import { WorkSearchSortForm } from "../../domain/models/dto/WorkDTO";
import { useSearchParams } from "react-router-dom";
import { SortingOrder } from "../../domain/enums/common/SortingOrderEnum";
import { useEffect } from "react";
import _ from "lodash";

enum WorksSearchParams {
    TITLE = "title",
    COUNTRY = "country",
    CITY = "city",
    ADDRESS = "address",
    CATEGORIES = "categories",
    SHOW_INACTIVE = "showInactive",
    SHOW_ONLY_ACTIVE = "showOnlyActive",
    SHOW_ONLY_INACTIVE = "showOnlyInactive",
    PRICE = "price",
    DISTANCE = "distance",
    START_FROM = "startFrom",
}

export const useUrlWorksSearchParams = (): WorkSearchSortForm => {
    const [searchParams] = useSearchParams();
    return {
        title: searchParams.get(WorksSearchParams.TITLE),
        address: {
            country: searchParams.get(WorksSearchParams.COUNTRY) ?? undefined,
            city: searchParams.get(WorksSearchParams.CITY) ?? undefined,
            address: searchParams.get(WorksSearchParams.ADDRESS) ?? undefined,
        },
        categories: searchParams
            .get(WorksSearchParams.CATEGORIES)
            ?.split(",")
            .map((str) => parseInt(str))
            .filter((num) => !isNaN(num))
            .map((category) => {
                return { category };
            }),
        showInactive:
            searchParams.get(WorksSearchParams.SHOW_INACTIVE) === "true",
        showOnlyActive:
            searchParams.get(WorksSearchParams.SHOW_ONLY_ACTIVE) === "true",
        showOnlyInactive:
            searchParams.get(WorksSearchParams.SHOW_ONLY_INACTIVE) === "true",
        price: searchParams.get(WorksSearchParams.PRICE) as SortingOrder,
        distance: searchParams.get(WorksSearchParams.DISTANCE) as SortingOrder,
        startFrom: searchParams.get(
            WorksSearchParams.START_FROM
        ) as SortingOrder,
    };
};

export const useWorksSearchParams = (form: FormikProps<WorkSearchSortForm>) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const handleSearchParam = (
        urlParams: URLSearchParams,
        param: WorksSearchParams,
        key: keyof WorkSearchSortForm,
        value?: number | string
    ) => {
        const currentValue = value || _.get(form, `values.${key}`);
        if (
            !currentValue ||
            currentValue === SortingOrder.DEFAULT ||
            (Array.isArray(currentValue) && !currentValue.length)
        ) {
            urlParams.delete(param);
        } else {
            urlParams.set(param, currentValue.toString());
        }
        return urlParams;
    };

    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        handleSearchParam(newParams, WorksSearchParams.TITLE, "title");
        handleSearchParam(
            newParams,
            WorksSearchParams.COUNTRY,
            "address.country" as keyof WorkSearchSortForm
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.CITY,
            "address.city" as keyof WorkSearchSortForm
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.ADDRESS,
            "address.address" as keyof WorkSearchSortForm
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.CATEGORIES,
            "categories",
            _.get(form, "values.categories", [])
                .map((item) => item.category)
                .join(",")
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.SHOW_INACTIVE,
            "showInactive"
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.SHOW_ONLY_ACTIVE,
            "showOnlyActive"
        );
        handleSearchParam(
            newParams,
            WorksSearchParams.SHOW_ONLY_INACTIVE,
            "showOnlyInactive"
        );
        handleSearchParam(newParams, WorksSearchParams.PRICE, "price");
        handleSearchParam(newParams, WorksSearchParams.DISTANCE, "distance");
        handleSearchParam(newParams, WorksSearchParams.START_FROM, "startFrom");

        setSearchParams(newParams);
    }, [form.isSubmitting]);

    return {
        resetUrlParams: () => {
            const newParams = new URLSearchParams();
            setSearchParams(newParams);
        },
    };
};
